import React from 'react';
import { Link } from 'react-router-dom';
import { ThreadSummary } from '../types/user';

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? 'Just now' : `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

interface MessageHistorySectionProps {
  conversations: ThreadSummary[];
}

const MessageHistorySection: React.FC<MessageHistorySectionProps> = ({ conversations }) => {
  return (
    <section className="p-6">
      <h2 className="font-title-lg text-on-surface mb-4">My conversations</h2>
      {conversations.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No conversations yet. Start by messaging a contractor on a job.</p>
      ) : (
        <div className="space-y-2">
          {conversations.map(thread => (
            <Link
              key={`${thread.jobId}-${thread.contractorProfileId}`}
              to={`/messages?jobId=${thread.jobId}&userId=${thread.otherUserId}`}
              className="flex items-center gap-4 p-4 rounded-xl bg-surface-container hover:bg-surface-container-high transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {thread.otherUserProfilePictureUrl ? (
                  <img src={thread.otherUserProfilePictureUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-primary text-[20px]" data-icon="person">person</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-on-surface truncate">{thread.otherUserDisplayName}</p>
                  <span className="text-xs text-on-surface-variant flex-shrink-0">{timeAgo(thread.lastMessageTimestamp)}</span>
                </div>
                <p className="text-xs text-on-surface-variant truncate mt-0.5">{thread.jobTitle}</p>
                <p className="text-xs text-on-surface-variant truncate opacity-70">{thread.lastMessageContent}</p>
              </div>
              {thread.unreadCount > 0 && (
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-on-primary text-[10px] font-bold flex items-center justify-center">
                  {thread.unreadCount}
                </span>
              )}
              {thread.isReadOnly && (
                <span className="flex-shrink-0 text-xs text-on-surface-variant bg-surface-container-high px-2 py-0.5 rounded-full">Closed</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default MessageHistorySection;
